import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useBackofficeAuth } from '@/contexts/BackofficeAuthContext';

export default function BackofficeLoginPage() {
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { login } = useBackofficeAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const from = (location.state as { from?: string })?.from || '/backoffice/dashboard';

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    setTimeout(() => {
      const result = login(email, senha);
      setIsLoading(false);
      if (result.success) {
        navigate(from, { replace: true });
      } else {
        setError(result.error || 'Erro ao fazer login');
      }
    }, 600);
  };

  const quickLogin = (demoEmail: string) => {
    setEmail(demoEmail);
    setSenha('123456');
    setError('');
  };

  return (
    <div className="min-h-screen bg-stone-900 flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-2xl bg-emerald-500 flex items-center justify-center mx-auto mb-4 shadow-lg">
            <div className="w-8 h-8 flex items-center justify-center">
              <i className="ri-shield-user-line text-2xl text-white"></i>
            </div>
          </div>
          <h1 className="text-2xl font-bold text-white">Backoffice</h1>
          <p className="text-sm text-stone-400 mt-1">getPoints - Portal de Gestão</p>
        </div>

        {/* Form */}
        <div className="bg-white rounded-2xl p-8 shadow-xl">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-100 rounded-lg flex items-center space-x-2">
              <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                <i className="ri-error-warning-line text-red-500 text-sm"></i>
              </div>
              <p className="text-xs text-red-600 font-medium">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1.5">Email</label>
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
                  <i className="ri-mail-line text-gray-400 text-sm"></i>
                </div>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="seu.email@getpoints.com.br"
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 transition-all"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1.5">Senha</label>
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
                  <i className="ri-lock-line text-gray-400 text-sm"></i>
                </div>
                <input
                  type="password"
                  value={senha}
                  onChange={(e) => setSenha(e.target.value)}
                  placeholder="••••••"
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 transition-all"
                  required
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm rounded-lg transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
            >
              {isLoading ? (
                <span className="flex items-center justify-center space-x-2">
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  <span>Entrando...</span>
                </span>
              ) : (
                'Entrar'
              )}
            </button>
          </form>

          {/* Quick login for demo */}
          <div className="mt-6 pt-4 border-t border-gray-100">
            <p className="text-[10px] text-gray-400 text-center mb-3 uppercase tracking-wider">Acesso rápido (demo)</p>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => quickLogin('admin@getpoints.com.br')}
                className="p-2 bg-stone-50 hover:bg-stone-100 rounded-lg text-[10px] text-stone-600 font-medium transition-all cursor-pointer text-left"
              >
                <span className="font-bold text-stone-800 block">Admin</span>
                <span className="text-stone-400">admin@...</span>
              </button>
              <button
                onClick={() => quickLogin('suporte@getpoints.com.br')}
                className="p-2 bg-stone-50 hover:bg-stone-100 rounded-lg text-[10px] text-stone-600 font-medium transition-all cursor-pointer text-left"
              >
                <span className="font-bold text-stone-800 block">Suporte</span>
                <span className="text-stone-400">suporte@...</span>
              </button>
              <button
                onClick={() => quickLogin('financeiro@getpoints.com.br')}
                className="p-2 bg-stone-50 hover:bg-stone-100 rounded-lg text-[10px] text-stone-600 font-medium transition-all cursor-pointer text-left"
              >
                <span className="font-bold text-stone-800 block">Financeiro</span>
                <span className="text-stone-400">financeiro@...</span>
              </button>
              <button
                onClick={() => quickLogin('operador@getpoints.com.br')}
                className="p-2 bg-stone-50 hover:bg-stone-100 rounded-lg text-[10px] text-stone-600 font-medium transition-all cursor-pointer text-left"
              >
                <span className="font-bold text-stone-800 block">Operador</span>
                <span className="text-stone-400">operador@...</span>
              </button>
            </div>
            <p className="text-[10px] text-gray-300 text-center mt-2">Senha padrão: 123456</p>
          </div>
        </div>
      </div>
    </div>
  );
}